import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import { Icon } from "@iconify/react/dist/iconify.js";
import { format } from "date-fns";

const NotificationsListLayer = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  // Function to fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const response = await api.notifications.getUserNotifications(
        user.id,
        currentPage,
        limit
      );

      if (response.success && response.data) {
        setNotifications(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
        }

        // Get unread count
        const unreadResponse = await api.notifications.getUnreadCount(user.id);
        if (unreadResponse.success) {
          setUnreadCount(unreadResponse.data.count);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await api.notifications.markAsRead(notificationId);

      if (response.success) {
        // Update local state
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification.id === notificationId
              ? { ...notification, isRead: true }
              : notification
          )
        );

        // Update unread count
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      const response = await api.notifications.markAllAsRead(user.id);

      if (response.success) {
        // Update local state
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) => ({
            ...notification,
            isRead: true,
          }))
        );

        // Update unread count
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, "MMM d, yyyy 'at' h:mm a");
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "REMINDER":
        return "mdi:clock-time-eight-outline";
      case "ACHIEVEMENT":
        return "bitcoin-icons:verify-outline";
      case "UPDATE":
        return "ph:megaphone-simple";
      case "PROMOTIONAL":
        return "mdi:star-outline";
      default:
        return "iconoir:bell";
    }
  };

  // Get notification background based on type
  const getNotificationBackground = (type) => {
    switch (type) {
      case "REMINDER":
        return "bg-info-subtle text-info-main";
      case "ACHIEVEMENT":
        return "bg-success-subtle text-success-main";
      case "UPDATE":
        return "bg-warning-subtle text-warning-main";
      case "PROMOTIONAL":
        return "bg-danger-subtle text-danger-main";
      default:
        return "bg-primary-subtle text-primary-main";
    }
  };

  // Generate pagination buttons
  const generatePaginationButtons = () => {
    const buttons = [];

    // Previous button
    buttons.push(
      <li
        key="prev"
        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <Icon icon="iconoir:nav-arrow-left" className="icon" />
        </button>
      </li>
    );

    // Page buttons
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        buttons.push(
          <li
            key={i}
            className={`page-item ${currentPage === i ? "active" : ""}`}
          >
            <button className="page-link" onClick={() => setCurrentPage(i)}>
              {i}
            </button>
          </li>
        );
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        buttons.push(
          <li key={i} className="page-item disabled">
            <button className="page-link">...</button>
          </li>
        );
      }
    }

    // Next button
    buttons.push(
      <li
        key="next"
        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          <Icon icon="iconoir:nav-arrow-right" className="icon" />
        </button>
      </li>
    );

    return buttons;
  };

  // Fetch notifications when component mounts or when page changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, currentPage, limit]);

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden">
      <div className="card-header bg-primary-50 py-16 px-24 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-primary-light text-lg fw-semibold">
          Notifications
        </h5>
        <div className="d-flex align-items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="btn btn-sm btn-outline-primary"
            >
              Mark All as Read
            </button>
          )}
          <span className="badge bg-primary-600 text-white">
            {unreadCount} Unread
          </span>
        </div>
      </div>
      <div className="card-body p-24">
        {isLoading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center p-5">
            <Icon
              icon="iconoir:bell"
              className="text-neutral-300 mb-3"
              style={{ fontSize: "64px" }}
            />
            <h4>No Notifications</h4>
            <p className="text-neutral-500">
              You don't have any notifications at the moment.
            </p>
          </div>
        ) : (
          <div>
            <div className="notification-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item p-3 mb-3 border radius-8 d-flex align-items-start gap-3 ${
                    !notification.isRead
                      ? "border-primary-100 bg-primary-50"
                      : "border-neutral-200"
                  }`}
                >
                  <div
                    className={`notification-icon w-44-px h-44-px rounded-circle d-flex justify-content-center align-items-center ${getNotificationBackground(
                      notification.type
                    )}`}
                  >
                    <Icon
                      icon={getNotificationIcon(notification.type)}
                      className="icon text-xl"
                    />
                  </div>
                  <div className="notification-content flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="fw-semibold mb-0">{notification.title}</h6>
                      <span className="text-secondary-light text-sm">
                        {formatDate(notification.createdAt)}
                      </span>
                    </div>
                    <p className="mb-2">{notification.message}</p>
                    {!notification.isRead && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="btn btn-sm btn-link text-primary p-0"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="d-flex align-items-center">
                <span className="me-2">Show</span>
                <select
                  className="form-select form-select-sm"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="ms-2">entries</span>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  {generatePaginationButtons()}
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsListLayer;
